/****************************************************
 *
 * 公共内容区加载：路由作用域、并发任务与防闪烁。
 *
 * @author Francis
 * @since 2026-09-07 16:40
 **************************************************/
import { nextTick, shallowRef } from 'vue'
import { capturePageRequest } from './pageReadScope.mjs'

/** 创建可独立验证的加载管理器；所有 HTTP 实例共享同一个运行时管理器。 */
export function createContentLoadingManager(clock = {
  now: () => Date.now(),
  setTimeout: (callback, delay) => setTimeout(callback, delay),
  clearTimeout: (timer) => clearTimeout(timer),
}) {
  let active = null
  let revision = 0
  const listeners = new Set()
  const snapshot = () => ({ scopeId: active?.id ?? null, visible: Boolean(active?.visible) })
  const notify = () => listeners.forEach((listener) => listener(snapshot()))
  const clearTimers = (scope) => {
    if (scope.showTimer !== null) clock.clearTimeout(scope.showTimer)
    if (scope.hideTimer !== null) clock.clearTimeout(scope.hideTimer)
    scope.showTimer = scope.hideTimer = null
  }
  const manager = {
    snapshot,
    subscribe(listener) {
      listeners.add(listener)
      listener(snapshot())
      return () => listeners.delete(listener)
    },
    activate() {
      if (active) clearTimers(active)
      active = {
        id: ++revision, tasks: new Set(), visible: false, shownAt: 0,
        showTimer: null, hideTimer: null,
      }
      notify()
      return active.id
    },
    deactivate(scopeId) {
      if (active?.id !== scopeId) return
      clearTimers(active)
      active = null
      notify()
    },
    isActive(scopeId) {
      return active?.id === scopeId
    },
    begin(scopeId = active?.id) {
      const scope = active
      if (!scope || scope.id !== scopeId) return () => {}
      const token = Symbol('content-task')
      scope.tasks.add(token)
      if (scope.hideTimer !== null) clock.clearTimeout(scope.hideTimer)
      scope.hideTimer = null
      if (!scope.visible && scope.showTimer === null) {
        scope.showTimer = clock.setTimeout(() => {
          scope.showTimer = null
          if (active !== scope || !scope.tasks.size) return
          scope.visible = true
          scope.shownAt = clock.now()
          notify()
        }, 150)
      }
      return () => {
        if (!scope.tasks.delete(token) || active !== scope || scope.tasks.size) return
        if (scope.showTimer !== null) clock.clearTimeout(scope.showTimer)
        scope.showTimer = null
        if (!scope.visible) return
        scope.hideTimer = clock.setTimeout(() => {
          scope.hideTimer = null
          if (active !== scope || scope.tasks.size) return
          scope.visible = false
          notify()
        }, 0)
      }
    },
  }
  return manager
}

export const contentLoadingManager = createContentLoadingManager()
export const contentLoadingState = shallowRef(contentLoadingManager.snapshot())
contentLoadingManager.subscribe((state) => { contentLoadingState.value = state })

/** 在 Vue 更新后让出一次绘制机会，后台标签页使用有界退路，避免等待挂起。 */
export async function paintContentFrame() {
  await nextTick()
  if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') return
  await new Promise((resolve) => {
    let frame = null
    let afterFrame = null
    const finish = () => {
      clearTimeout(fallback)
      if (afterFrame !== null) clearTimeout(afterFrame)
      if (frame !== null) cancelAnimationFrame(frame)
      resolve()
    }
    const fallback = setTimeout(finish, 100)
    frame = requestAnimationFrame(() => { afterFrame = setTimeout(finish, 0) })
  })
}

/** 页面逻辑任务覆盖计算、请求和结果绘制；晚返回的旧路由任务不影响新页面。 */
export async function withContentLoading(work, scopeId = contentLoadingState.value.scopeId) {
  const finish = contentLoadingManager.begin(scopeId)
  try {
    return await work({
      scopeId,
      isActive: () => scopeId === null || contentLoadingManager.isActive(scopeId),
      paint: paintContentFrame,
    })
  } finally {
    try {
      await paintContentFrame()
    } finally {
      finish()
    }
  }
}

/** 从请求配置剥离 UI 元数据，覆盖请求准备、加密异常、取消及所有响应路径。 */
export function withContentRequest(options, send) {
  const captured = capturePageRequest(options)
  const {
    loading = captured.context.background ? 'local' : 'content',
    loadingScope = captured.context.loadingScope ?? contentLoadingState.value.scopeId,
    ...requestOptions
  } = captured.options
  const finish = loading === 'content' ? contentLoadingManager.begin(loadingScope) : () => {}
  try {
    captured.context.assertActive()
    return Promise.resolve(send(requestOptions, captured.context)).finally(() => {
      captured.cleanup()
      // 不推迟业务响应；释放加载放在调用方回填数据后的绘制阶段。
      void paintContentFrame().then(finish, finish)
    })
  } catch (error) {
    captured.cleanup()
    finish()
    return Promise.reject(error)
  }
}

/**
 * 禁用业务子树并保留嵌在页面内的独立弹窗。Element Plus Dialog 默认不传送到 body，
 * 因此不能直接给整个 router-view 的祖先设置 inert。
 */
export function createContentInteractionGuard(root) {
  const protectedSelector = '.el-overlay, [role="dialog"], [role="alertdialog"]'
  let locked = new Map(), observer = null
  const refresh = () => {
    const targets = new Set()
    const visit = (element) => {
      if (element.matches(protectedSelector)) return
      if (!element.querySelector(protectedSelector)) {
        targets.add(element)
        return
      }
      Array.from(element.children).forEach(visit)
    }
    Array.from(root.children).forEach(visit)
    locked.forEach((previous, element) => {
      if (targets.has(element)) return
      if (previous === null) element.removeAttribute('inert')
      else element.setAttribute('inert', previous)
      locked.delete(element)
    })
    targets.forEach((element) => {
      if (locked.has(element)) return
      locked.set(element, element.getAttribute('inert'))
      element.setAttribute('inert', '')
    })
  }
  return {
    isProtected: (element) => Boolean(element?.closest?.(protectedSelector)),
    start() {
      if (observer) return
      refresh()
      observer = new MutationObserver(refresh)
      observer.observe(root, { childList: true, subtree: true })
    },
    stop() {
      observer?.disconnect()
      observer = null
      locked.forEach((previous, element) => {
        if (previous === null) element.removeAttribute('inert')
        else element.setAttribute('inert', previous)
      })
      locked = new Map()
    },
  }
}
